import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'

import { PagesData } from '@shared/models/pages-data.model'
import { ApiService } from '@shared/services/api.service'
import { Reseller, ResellerUser } from '@shared/models/reseller.model'
import { FilterValues } from '@shared/models/filter-values.model'
import { NoteHistory } from '@shared/models/note.model'
import { ExportFileTypes } from '@shared/models/export-file-types.enum'
import { toFormData } from '@app/core/helpers/to-form-data'

@Injectable({ providedIn: 'root' })
export class ResellerService {
  constructor(private apiService: ApiService) {}

  getResellerById(id: number | string): Observable<Reseller> {
    return this.apiService.get(`/reseller/${id}`).pipe(map((resp) => new Reseller(<Reseller>resp['data'])))
  }

  getResellerUsers(
    id: number | string,
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: FilterValues,
    fields?: (keyof ResellerUser)[],
  ): Observable<PagesData<ResellerUser>> {
    return this.apiService.getPageable<ResellerUser>(
      `/reseller/${id}/users`,
      pageIndex,
      pageSize,
      sort,
      filterValues,
      fields,
    ) as Observable<PagesData<ResellerUser>>
  }

  getResellerUsersList(resellerId: number | string, filterValues?: FilterValues): Observable<ResellerUser[]> {
    const fields: (keyof ResellerUser)[] = ['id', 'name', 'surname', 'userGroups', 'team']

    return this.getResellerUsers(resellerId, 0, 1000, null, filterValues, fields).pipe(
      map((response) => response['data'].map((user) => new ResellerUser(user))),
    )
  }

  addResellerUser(id: number | string, user: Partial<ResellerUser>): Observable<ResellerUser> {
    return this.apiService.post(`/reseller/${id}/users`, user).pipe(map((resp) => <ResellerUser>resp['data']))
  }

  getResellerUserById(resellerId: number | string, userId: string | number): Observable<ResellerUser> {
    return this.apiService.get(`/reseller/${resellerId}/users/${userId}`).pipe(map((resp) => new ResellerUser(<ResellerUser>resp['data'])))
  }

  editResellerUser(resellerId: number | string, userId: number | string, user: Partial<ResellerUser>): Observable<ResellerUser> {
    return this.apiService.post(`/reseller/${resellerId}/users/${userId}`, toFormData(user)).pipe(map((resp) => <ResellerUser>resp['data']))
  }

  deleteResellerUser(resellerId: number | string, userId: number | string): Observable<void> {
    return this.apiService.delete(`/reseller/${resellerId}/users/${userId}`)
  }

  getResellerNotes(resellerId: number | string, note: string): Observable<NoteHistory[]> {
    return this.apiService.get(`/reseller-notes/${resellerId}/${note}`).pipe(map((resp) => <NoteHistory[]>resp['data']))
  }

  getResellerStatusHistory(id: number | string): Observable<History> {
    return this.apiService.get(`/reseller/${id}/history/status`).pipe(map((resp) => <History>resp['data']))
  }

  restoreUser(userId: number): Observable<ResellerUser> {
    return this.apiService.post(`/users/${userId}/restore`, {}).pipe(map((resp) => <ResellerUser>resp['data']))
  }

  exportResellerUsers(
    id: string | number,
    columns: string[],
    sort: string,
    filterValues: FilterValues,
    type: ExportFileTypes,
  ): Observable<string> {
    return this.apiService.exportFile(`/reseller/${id}/users/${type}`, columns, sort, filterValues)
  }
}
